import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, User, Car, Lock, AlertTriangle, Loader2, Check, MapPin, Wallet } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Country, City } from 'country-state-city';
import { SearchableSelect } from './SearchableSelect';

const VEHICLE_TYPES = [
  'economy', 'comfort', 'business', 'premium', 'vip', 'suv', 'van', 'minibus', 'bus'
];

export function ProfileSettingsModal({ onClose, t, driverProfile: externalDriverProfile, onProfileUpdated }) {
  const { user } = useAuthStore();
  const isDriver = user?.role === 'driver';
  const [activeSection, setActiveSection] = useState('profile');
  const [loadedProfile, setLoadedProfile] = useState(externalDriverProfile || null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Self-fetch driver profile if not passed from parent
  useEffect(() => {
    if (isDriver && !loadedProfile) {
      setProfileLoading(true);
      api.get('/driver/profile')
        .then(res => { if (res.data) setLoadedProfile(res.data); })
        .catch(() => {})
        .finally(() => setProfileLoading(false));
    }
  }, [isDriver, loadedProfile]);

  // Use the loaded profile (from parent or self-fetched)
  const driverProfile = loadedProfile;

  // Profile fields
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [phoneChanged, setPhoneChanged] = useState(false);
  const [phoneVerifyMode, setPhoneVerifyMode] = useState(false);
  const [phoneVerifyCode, setPhoneVerifyCode] = useState('');
  const originalPhone = useRef(user?.phone || '');
  const [region, setRegion] = useState(driverProfile?.region || '');
  const [selectedCountry, setSelectedCountry] = useState(driverProfile?.country || '');

  // Egyptian region detection (frontend fallback if backend hasn't set country yet)
  const EGYPT_REGION_NAMES = ['hurghada', 'sharm', 'sharm el sheikh', 'cairo', 'kairo', 'luxor',
    'aswan', 'assuan', 'alexandria', 'marsa alam', 'marsa_alam', 'el gouna', 'dahab', 'taba',
    'ain sokhna', 'port said', 'suez', 'ismailia', 'giza', 'gizeh', 'makadi', 'sahl hasheesh',
    'safaga', 'qena', 'soma bay', 'nuweiba'];

  // Detect country from region if not set
  const effectiveCountry = selectedCountry || (() => {
    const r = (driverProfile?.region || '').toLowerCase().trim();
    return EGYPT_REGION_NAMES.includes(r) ? 'EG' : '';
  })();
  const isEgyptian = effectiveCountry === 'EG';

  // Helper: only allow phone-like values (no emails)
  const cleanPhoneField = (val) => (!val || val.includes('@')) ? '' : val;

  // Payment account fields (driver only)
  const [paypalEmail, setPaypalEmail] = useState(driverProfile?.paypal_email || '');
  const [instapayNumber, setInstapayNumber] = useState(cleanPhoneField(driverProfile?.instapay_number));
  const [vodafoneCashNumber, setVodafoneCashNumber] = useState(cleanPhoneField(driverProfile?.vodafone_cash_number));

  // Vehicle fields (driver only)
  const [vehicle, setVehicle] = useState({
    make: '', model: '', year: 2024, color: '',
    plate_number: '', vehicle_type: 'economy', passengers: 4, luggage: 3,
  });

  // Cities for current driver country as searchable options
  const cityOptions = useMemo(() => {
    if (!isDriver || !effectiveCountry) return [];
    const cities = City.getCitiesOfCountry(effectiveCountry);
    if (!cities || cities.length === 0) return [];
    const seen = new Set();
    return cities
      .filter(c => { if (seen.has(c.name)) return false; seen.add(c.name); return true; })
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(c => ({ value: c.name, label: `${c.name}${c.stateCode ? ` (${c.stateCode})` : ''}` }));
  }, [isDriver, effectiveCountry]);

  const countryName = useMemo(() => {
    if (!effectiveCountry) return '';
    const c = Country.getCountryByCode(effectiveCountry);
    return c ? `${c.flag} ${c.name}` : effectiveCountry;
  }, [effectiveCountry]);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Populate vehicle and payment fields when driver profile is loaded
  useEffect(() => {
    if (driverProfile?.vehicle) {
      setVehicle({
        make: driverProfile.vehicle.make || '',
        model: driverProfile.vehicle.model || '',
        year: driverProfile.vehicle.year || 2024,
        color: driverProfile.vehicle.color || '',
        plate_number: driverProfile.vehicle.plate_number || '',
        vehicle_type: driverProfile.vehicle.vehicle_type || 'economy',
        passengers: driverProfile.vehicle.passengers || 4,
        luggage: driverProfile.vehicle.luggage || 3,
      });
    }
    if (driverProfile) {
      setRegion(driverProfile.region || '');
      setSelectedCountry(driverProfile.country || '');
      setPaypalEmail(driverProfile.paypal_email || '');
      setInstapayNumber(cleanPhoneField(driverProfile.instapay_number));
      setVodafoneCashNumber(cleanPhoneField(driverProfile.vodafone_cash_number));
    }
  }, [driverProfile]);

  const handleSaveProfile = async () => {
    // Check if phone number changed - require SMS verification
    if (phone && phone !== originalPhone.current && !phoneVerifyMode) {
      setSaving(true);
      try {
        await api.post('/auth/send-phone-verification', { phone });
        setPhoneVerifyMode(true);
        setMessage({ text: `SMS-Code an ${phone.slice(0,6)}***${phone.slice(-2)} gesendet`, type: 'info' });
      } catch (err) {
        setMessage({ text: err.response?.data?.detail || 'SMS konnte nicht gesendet werden', type: 'error' });
      } finally { setSaving(false); }
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = { name, email, phone };
      if (isDriver) {
        payload.vehicle = vehicle;
        payload.region = region;
        payload.paypal_email = paypalEmail;
        // Send detected country so backend persists it
        if (effectiveCountry && !selectedCountry) {
          payload.country = effectiveCountry;
        }
        if (isEgyptian) {
          payload.instapay_number = instapayNumber;
          payload.vodafone_cash_number = vodafoneCashNumber;
        }
      }
      const res = await api.put('/auth/profile', payload);
      // Update local storage with new user data
      if (res.data.user) {
        localStorage.setItem('user', JSON.stringify(res.data.user));
      }
      if (res.data.requires_reapproval) {
        setMessage({ text: t('profile_reapproval_triggered'), type: 'warning' });
      } else {
        setMessage({ text: t('profile_updated'), type: 'success' });
      }
      if (onProfileUpdated) onProfileUpdated(res.data);
    } catch (err) {
      setMessage({ text: err.response?.data?.detail || 'Fehler', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePhoneVerifyInProfile = async () => {
    if (!phoneVerifyCode || phoneVerifyCode.length !== 6) {
      setMessage({ text: 'Bitte 6-stelligen Code eingeben', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/verify-phone', { phone, code: phoneVerifyCode });
      originalPhone.current = phone;
      setPhoneVerifyMode(false);
      setPhoneVerifyCode('');
      setMessage({ text: 'Mobilnummer verifiziert. Speichere Profil...', type: 'success' });
      // Now proceed with actual save
      handleSaveProfile();
    } catch (err) {
      setMessage({ text: err.response?.data?.detail || 'Falscher SMS-Code', type: 'error' });
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage({ text: t('profile_password_mismatch'), type: 'error' });
      return;
    }
    if (newPassword.length < 6) {
      setMessage({ text: 'Mindestens 6 Zeichen', type: 'error' });
      return;
    }
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      await api.put('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setMessage({ text: t('profile_password_changed'), type: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setMessage({ text: err.response?.data?.detail || 'Fehler', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const msgColor = message.type === 'success' ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : message.type === 'warning' ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-red-700 bg-red-50 border-red-200';

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 sm:flex sm:items-center sm:justify-center sm:p-4" onClick={onClose}>
      <div className="bg-white sm:rounded-2xl shadow-2xl w-full h-full sm:h-auto sm:max-w-lg sm:max-h-[90vh] overflow-y-auto overflow-x-hidden" onClick={e => e.stopPropagation()} data-testid="profile-settings-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800" data-testid="profile-modal-title">{t('profile_settings')}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="profile-close-btn">
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Section tabs */}
        <div className="flex border-b border-slate-100">
          <button
            onClick={() => { setActiveSection('profile'); setMessage({ text: '', type: '' }); }}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeSection === 'profile' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-profile"
          >
            <User size={16} />
            {t('profile_edit')}
          </button>
          <button
            onClick={() => { setActiveSection('password'); setMessage({ text: '', type: '' }); }}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeSection === 'password' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-password"
          >
            <Lock size={16} />
            {t('profile_change_password')}
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {message.text && (
            <div className={`p-3 rounded-lg border mb-4 text-sm flex items-center gap-2 ${msgColor}`} data-testid="profile-message">
              {message.type === 'warning' ? <AlertTriangle size={16} /> : message.type === 'success' ? <Check size={16} /> : <AlertTriangle size={16} />}
              {message.text}
            </div>
          )}

          {profileLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-orange-500" />
            </div>
          )}

          <div className="space-y-4" style={{ display: !profileLoading && activeSection === 'profile' ? 'block' : 'none' }}>
              {isDriver && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-start gap-2" data-testid="reapproval-warning">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  {t('profile_reapproval_warning')}
                </div>
              )}

              {/* User fields */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_name')}</label>
                <input
                  type="text" value={name} onChange={e => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_email')}</label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                  data-testid="profile-email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_phone')}</label>
                {user?.role === 'admin' && (
                  <p className="text-xs text-blue-600 mb-1.5 flex items-center gap-1">
                    <Lock size={12} />
                    Diese Nummer wird für die 2FA-Anmeldung verwendet
                  </p>
                )}
                <input
                  type="tel" value={phone} onChange={e => { setPhone(e.target.value); if (e.target.value !== originalPhone.current) setPhoneChanged(true); }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                  placeholder="+41 79 xxx xx xx"
                  data-testid="profile-phone-input"
                />
              </div>

              {/* Country & Region for drivers */}
              {isDriver && driverProfile && (
                <div className="pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={16} className="text-slate-500" />
                    <span className="text-sm font-semibold text-slate-700">Standort</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Land</label>
                      <div className="w-full px-3 py-2 border border-slate-100 rounded-lg text-sm bg-slate-50 text-slate-500" data-testid="profile-country-display">
                        {countryName || 'Nicht gesetzt'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Region / Stadt</label>
                      {cityOptions.length > 0 ? (
                        <SearchableSelect
                          options={cityOptions}
                          value={region}
                          onChange={setRegion}
                          placeholder="Stadt suchen..."
                          testId="profile-region-select"
                        />
                      ) : (
                        <input
                          type="text" value={region} onChange={e => setRegion(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          placeholder="Region eingeben"
                          data-testid="profile-region-input"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Vehicle fields for drivers */}
              {isDriver && driverProfile && (
                <>
                  <div className="pt-2 border-t border-slate-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Car size={16} className="text-slate-500" />
                      <span className="text-sm font-semibold text-slate-700">{t('profile_vehicle_info')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_vehicle_make')}</label>
                        <input
                          type="text" value={vehicle.make} onChange={e => setVehicle(v => ({ ...v, make: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          data-testid="vehicle-make-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_vehicle_model')}</label>
                        <input
                          type="text" value={vehicle.model} onChange={e => setVehicle(v => ({ ...v, model: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          data-testid="vehicle-model-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_vehicle_color')}</label>
                        <input
                          type="text" value={vehicle.color} onChange={e => setVehicle(v => ({ ...v, color: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          data-testid="vehicle-color-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_vehicle_plate')}</label>
                        <input
                          type="text" value={vehicle.plate_number} onChange={e => setVehicle(v => ({ ...v, plate_number: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          data-testid="vehicle-plate-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_vehicle_year')}</label>
                        <input
                          type="number" value={vehicle.year} onChange={e => setVehicle(v => ({ ...v, year: parseInt(e.target.value) || 2024 }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          data-testid="vehicle-year-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_vehicle_type')}</label>
                        <select
                          value={vehicle.vehicle_type} onChange={e => setVehicle(v => ({ ...v, vehicle_type: e.target.value }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none bg-white"
                          data-testid="vehicle-type-select"
                        >
                          {VEHICLE_TYPES.map(vt => <option key={vt} value={vt}>{vt.charAt(0).toUpperCase() + vt.slice(1)}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_vehicle_passengers')}</label>
                        <input
                          type="number" min={1} max={50} value={vehicle.passengers}
                          onChange={e => setVehicle(v => ({ ...v, passengers: parseInt(e.target.value) || 4 }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          data-testid="vehicle-passengers-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_vehicle_luggage')}</label>
                        <input
                          type="number" min={0} max={50} value={vehicle.luggage}
                          onChange={e => setVehicle(v => ({ ...v, luggage: parseInt(e.target.value) || 3 }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          data-testid="vehicle-luggage-input"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Payment account fields for drivers */}
              {isDriver && driverProfile && (
                <div className="pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-2 mb-3">
                    <Wallet size={16} className="text-slate-500" />
                    <span className="text-sm font-semibold text-slate-700">Zahlungskonten</span>
                    <span className="text-xs text-slate-400">(optional)</span>
                  </div>
                  <div className="space-y-3">
                    {/* PayPal - all drivers worldwide */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        PayPal-Konto
                        <span className="ml-1.5 text-xs font-normal text-slate-400">E-Mail-Adresse</span>
                      </label>
                      <input
                        type="email" value={paypalEmail} onChange={e => setPaypalEmail(e.target.value)}
                        placeholder="paypal@beispiel.com"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                        data-testid="paypal-email-input"
                      />
                    </div>
                    {/* InstaPay - only Egyptian drivers */}
                    {isEgyptian && (
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          InstaPay-Konto
                          <span className="ml-1.5 text-xs font-normal text-slate-400">Mobilnummer</span>
                        </label>
                        <input
                          type="tel" value={instapayNumber} onChange={e => setInstapayNumber(e.target.value)}
                          placeholder="01xxxxxxxxx"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          data-testid="instapay-number-input"
                        />
                      </div>
                    )}
                    {/* Vodafone Cash - only Egyptian drivers */}
                    {isEgyptian && (
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Vodafone Cash-Konto
                          <span className="ml-1.5 text-xs font-normal text-slate-400">Mobilnummer</span>
                        </label>
                        <input
                          type="tel" value={vodafoneCashNumber} onChange={e => setVodafoneCashNumber(e.target.value)}
                          placeholder="01xxxxxxxxx"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                          data-testid="vodafone-cash-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Phone verification for number change */}
              {phoneVerifyMode && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3" data-testid="phone-verify-profile-section">
                  <p className="text-sm text-blue-700 font-medium">SMS-Code eingeben zur Bestätigung der neuen Mobilnummer</p>
                  <div className="flex gap-2">
                    <input
                      type="text" value={phoneVerifyCode}
                      onChange={e => setPhoneVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="6-stelliger Code"
                      className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm text-center tracking-widest font-mono"
                      maxLength={6}
                      data-testid="phone-verify-profile-input"
                    />
                    <button onClick={handlePhoneVerifyInProfile} disabled={saving || phoneVerifyCode.length !== 6}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
                      data-testid="phone-verify-profile-submit">
                      {saving ? <Loader2 size={14} className="animate-spin" /> : 'Bestätigen'}
                    </button>
                  </div>
                  <button onClick={async () => { try { await api.post('/auth/send-phone-verification', { phone }); setMessage({ text: 'Neuer SMS-Code gesendet', type: 'info' }); } catch {} }}
                    className="text-xs text-blue-500 hover:underline" data-testid="resend-phone-profile-btn">
                    SMS erneut senden
                  </button>
                </div>
              )}

              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-profile-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {t('profile_save')}
              </button>
          </div>

          <div className="space-y-4" style={{ display: activeSection === 'password' ? 'block' : 'none' }}>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_current_password')}</label>
                <input
                  type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_new_password')}</label>
                <input
                  type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('profile_confirm_password')}</label>
                <input
                  type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 outline-none"
                  data-testid="confirm-password-input"
                />
              </div>
              <button
                onClick={handleChangePassword}
                disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="change-password-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {t('profile_change_password')}
              </button>
          </div>
        </div>
      </div>
    </div>
  );
}
